import { Typography, Button, Popconfirm, Space } from "antd";
import { BankOutlined, DeleteOutlined } from "@ant-design/icons";
import {
  formatMoney,
  formatDateTime,
  formatAccountNumber,
} from "../../utils/formatUtils";

const { Text } = Typography;

export function renderBankCell(value) {
  return (
    <Space size={10}>
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 text-slate-600">
        <BankOutlined />
      </div>
      <span className="font-medium text-slate-800">{value || "-"}</span>
    </Space>
  );
}

export function renderAccountNumberCell(value) {
  return (
    <span className="font-mono text-slate-700">
      {value ? formatAccountNumber(value) : "-"}
    </span>
  );
}

export function renderCopyableAccountNumberCell(value) {
  if (!value) return "-";

  return (
    <Text copyable={{ text: value }}>
      {formatAccountNumber(value)}
    </Text>
  );
}

export function renderDateTimeCell(value) {
  return <span className="text-slate-500">{formatDateTime(value)}</span>;
}

export function renderTransactionDescriptionCell(value) {
  return (
    <div className="max-w-[420px] whitespace-normal break-words text-sm text-slate-700">
      {value || "-"}
    </div>
  );
}

export function renderTextCell(value) {
  return (
    <div className="whitespace-normal break-words">
      <Text strong>{value || "-"}</Text>
    </div>
  );
}

export function renderPlainTextCell(value) {
  return <span>{value ?? "-"}</span>;
}

export function renderAmountCell(value, currency, strong = false) {
  const content = formatMoney(value, currency);

  return strong ? <Text strong>{content}</Text> : <Text>{content}</Text>;
}

export function renderDeleteAction(_, record, onDelete) {
  if (!record?.canDeleted) return null;

  return (
    <Popconfirm
      title="Устгах уу?"
      okText="Тийм"
      cancelText="Үгүй"
      onConfirm={() => onDelete(record.id)}
    >
      <Button type="text" danger icon={<DeleteOutlined />} />
    </Popconfirm>
  );
}

/* =========================
   COLUMN BUILDERS
========================= */

export function getTransactionDateColumn() {
  return {
    title: "Огноо",
    dataIndex: "transactionDate",
    key: "transactionDate",
    width: 180,
    align: "center",
    render: renderDateTimeCell,
  };
}

export function getAccountWithBankColumn() {
  return {
    title: "Байгууллагын нэр / Банк",
    key: "account_bank",
    width: 300,
    render: (_, record) => {
      const { bankAccountName, bank } = record;

      return (
        <div style={{ lineHeight: 1.4 }}>
          {/* Account Name (top) */}
          <div style={{ fontWeight: 500 }}>
            {renderTextCell(bankAccountName)}
          </div>

          {/* Bank (below) */}
          <div style={{ fontSize: 12, color: "#888" }}>
            {renderBankCell(bank)}
          </div>
        </div>
      );
    },
  };
}

export function getAccountNumberColumn() {
  return {
    title: "Дансны дугаар",
    dataIndex: "bankAccount",
    key: "bankAccount",
    width: 200,
    render: renderAccountNumberCell,
  };
}

export function getCounterpartyNameColumn() {
  return {
    title: "Харилцагчийн нэр",
    dataIndex: "counterpartyName",
    key: "counterpartyName",
    width: 220,
    render: renderTextCell,
  };
}

export function getCounterPartyAccountNumberColumn({ copyable = false } = {}) {
  return {
    title: "Харилцагчийн данс",
    dataIndex: "counterpartyAccount",
    key: "counterpartyAccount",
    width: 220,
    render: copyable
      ? renderCopyableAccountNumberCell
      : renderAccountNumberCell,
  };
}

export function getTransactionDescriptionColumn() {
  return {
    title: "Гүйлгээний утга",
    dataIndex: "transactionDescription",
    key: "transactionDescription",
    width: 320,
    ellipsis: true,
    render: renderTransactionDescriptionCell,
  };
}

export function getIncomeColumn(currency) {
  return {
    title: "Орлого",
    dataIndex: "income",
    key: "income",
    width: 140,
    align: "right",
    render: (value) => renderAmountCell(value, currency),
  };
}

export function getExpenseColumn(currency) {
  return {
    title: "Зарлага",
    dataIndex: "expense",
    key: "expense",
    width: 140,
    align: "right",
    render: (value) => renderAmountCell(value, currency),
  };
}

export function getRateColumn() {
  return {
    title: "Ханш",
    dataIndex: "exchangeRate",
    key: "exchangeRate",
    width: 100,
    align: "right",
    render: renderPlainTextCell,
  };
}

export function getBalanceColumn(currency, { strong = false } = {}) {
  return {
    title: "Үлдэгдэл",
    dataIndex: "balance",
    key: "balance",
    width: 140,
    align: "right",
    render: (value) => renderAmountCell(value, currency, strong),
  };
}

export function getDeleteActionColumn(onDelete) {
  return {
    title: "Устгах",
    key: "action",
    width: 80,
    align: "center",
    render: (_, record) => renderDeleteAction(_, record, onDelete),
  };
}